import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Tooltip } from "./tooltips.js";
import { handleUIEvent } from "./eventLogger.js";

const API_URL = process.env.REACT_APP_API_URL || "https://back.tontradingtools.com";

// Concentration pattern threshold (24% from _90_pattern_concentration.json)
const CONCENTRATION_THRESHOLD_1 = 0.24;

// Button style matches _13 module
const buttonStyle = (active) => ({
    background: active ? "#00b894" : "#232946",
    color: "#fff",
    border: "1px solid #444",
    borderRadius: 4,
    padding: "6px 16px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    marginRight: 8,
    marginBottom: 6,
    minWidth: 60,
});

function calculatePLAndMaxQtySeries(trades) {
    let position = 0;
    let totalCost = 0;
    let plSeries = [];
    let maxQtySeries = [];
    let runningMaxQty = 0;
    let runningPosEffectSeries = [];

    trades.forEach((trade) => {
        const qty = Math.abs(Number(trade.Qty));
        const price = Number(trade.price);
        const side = String(trade.Side).toUpperCase();
        const posEffect = String(trade.Pos_Effect || "").toUpperCase();

        if (side === "BUY") {
            if (posEffect === "TO OPEN" || !trade.Pos_Effect) {
                runningPosEffectSeries.push("LONG");
            } else if (posEffect === "TO CLOSE") {
                runningPosEffectSeries.push("LONG");
            } else {
                runningPosEffectSeries.push("");
            }
        } else if (side === "SELL") {
            if (posEffect === "TO OPEN") {
                runningPosEffectSeries.push("SHORT");
            } else if (posEffect === "TO CLOSE") {
                runningPosEffectSeries.push("SHORT");
            } else {
                runningPosEffectSeries.push("");
            }
        } else {
            runningPosEffectSeries.push("");
        }

        if (side === "BUY") {
            position += qty;
            totalCost += qty * price;
        } else if (side === "SELL") {
            if (position > 0) {
                const avgCost = totalCost / position;
                const sellQty = Math.min(qty, position);
                totalCost -= avgCost * sellQty;
                position -= sellQty;
            }
        }

        runningMaxQty = Math.max(runningMaxQty, Number(trade.RunningQty));
        maxQtySeries.push(runningMaxQty);

        let runningPL = 0;
        if (position > 0) {
            const avgCost = totalCost / position;
            runningPL = (price - avgCost) * position;
        }
        plSeries.push(runningPL);
    });
    return { plSeries, maxQtySeries, runningPosEffectSeries };
}

// CSV download helper
function downloadCSV(trades, plSeries) {
    if (!trades.length) return;
    const headers = [
        "Exec Time",
        "Side",
        "Qty",
        "Price",
        "Pos Effect",
        "Risk %",
        "Running Qty",
        "Running P&L"
    ];
    const rows = trades.map((trade, idx) => [
        trade.Exec_Time,
        trade.Side,
        trade.Qty,
        trade.price,
        trade.Pos_Effect || "",
        trade["Risk %"] || "",
        trade.RunningQty !== undefined ? trade.RunningQty : "",
        plSeries[idx] !== undefined ? plSeries[idx].toFixed(2) : ""
    ]);
    const csvContent =
        [headers.join(",")]
            .concat(rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")))
            .join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "intraday_trades.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

// Chart with mode switch
function RunningPLChart({ trades, plSeries, chartMode }) {
    const [hoverIdx, setHoverIdx] = useState(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    // Extract time, price, runningQty, and qty for each point
    const points = trades.map((trade, idx) => {
        let timeStr = "";
        if (trade.Exec_Time) {
            const parts = trade.Exec_Time.split(" ");
            timeStr = parts.length >= 5 ? parts[4] : trade.Exec_Time.slice(-5);
        }
        return {
            x: idx,
            y: chartMode === "total" ? plSeries[idx] : Number(trade.price),
            price: Number(trade.price),
            time: timeStr,
            runningQty: trade.RunningQty !== undefined ? trade.RunningQty : "",
            qty: trade.Qty !== undefined ? Math.abs(Number(trade.Qty)) : 1 // proportional sizing
        };
    });

    const width = 1000;
    const height = 600;
    const margin = 32;
    if (points.length === 0) return <div>No chart data.</div>;

    // Scale points
    const minY = Math.min(...points.map(p => p.y));
    const maxY = Math.max(...points.map(p => p.y));
    const yRange = maxY - minY || 1;
    const xStep = (width - margin * 2) / (points.length - 1 || 1);

    const getY = y => height - margin - ((y - minY) / yRange) * (height - margin * 2);
    const getX = i => margin + i * xStep;

    // Circle radius scaling
    const minQty = Math.min(...points.map(p => p.qty));
    const maxQty = Math.max(...points.map(p => p.qty));
    const minRadius = 4;
    const maxRadius = 18;
    function getRadius(qty) {
        if (maxQty === minQty) return minRadius;
        return minRadius + ((qty - minQty) / (maxQty - minQty)) * (maxRadius - minRadius);
    }

    // Build area paths for positive and negative regions (only for running total)
    let posArea = "";
    let negArea = "";
    let posStarted = false;
    let negStarted = false;

    // For price area graph
    let priceArea = "";
    if (chartMode === "price") {
        priceArea += `M${getX(0)},${getY(0)} `;
        for (let i = 0; i < points.length; i++) {
            priceArea += `L${getX(i)},${getY(points[i].y)} `;
        }
        priceArea += `L${getX(points.length - 1)},${getY(0)} Z`;
    }

    // Build a single area path from the line to the zero axis
    let totalArea = "";
    if (chartMode === "total") {
        totalArea += `M${getX(0)},${getY(0)} `;
        for (let i = 0; i < points.length; i++) {
            totalArea += `L${getX(i)},${getY(points[i].y)} `;
        }
        totalArea += `L${getX(points.length - 1)},${getY(0)} Z`;
    }

    // Add horizontal axis lines (5 lines: min, 1/4, mid, 3/4, max)
    const axisLines = [];
    for (let i = 0; i <= 4; i++) {
        const yValue = minY + (i * (maxY - minY) / 4);
        axisLines.push(
            <line
                key={i}
                x1={margin}
                y1={getY(yValue)}
                x2={width - margin}
                y2={getY(yValue)}
                stroke="#444"
                strokeDasharray="4,4"
                opacity={i === 2 ? 0.7 : 0.4}
            />
        );
    }

    // Find highest and lowest points
    let maxIdx = 0, minIdx = 0;
    points.forEach((p, idx) => {
        if (p.y > points[maxIdx].y) maxIdx = idx;
        if (p.y < points[minIdx].y) minIdx = idx;
    });

    // Custom tooltip
    let tooltip = null;
    if (hoverIdx !== null && points[hoverIdx]) {
        const p = points[hoverIdx];
        tooltip = (
            <div
                style={{
                    position: "absolute",
                    left: mousePos.x - 30,
                    top: mousePos.y - 40,
                    background: "#222",
                    color: "#fff",
                    border: "1px solid #00FFFF",
                    borderRadius: 6,
                    padding: "8px 12px",
                    fontSize: 14,
                    pointerEvents: "none",
                    zIndex: 8,
                    boxShadow: "0 2px 12px #00FFFF88"
                }}
            >
                <div><strong>Time:</strong> {p.time}</div>
                {chartMode === "total" ? (
                    <div><strong>P&L:</strong> {p.y.toFixed(2)}</div>
                ) : (
                    <div><strong>Price:</strong> {p.y.toFixed(2)}</div>
                )}
                <div><strong>Qty:</strong> {p.qty}</div>
                <div><strong>Running Qty:</strong> {p.runningQty}</div>

            </div>
        );
    }

    // Teal color for price area and axis
    const teal = "#00b894";
    const tealLight = "rgba(0,184,148,0.18)";

    return (
        <div style={{ position: "relative" }}>
            <svg width={width} height={height} style={{ background: "#222", borderRadius: 8, marginBottom: 16 }}>
                {/* Area fill for price mode */}
                {chartMode === "price" && (
                    <>
                        <path
                            d={priceArea}
                            fill={tealLight}
                            stroke="none"
                        />
                        {/* Bottom axis shading */}
                        <rect
                            x={margin}
                            y={getY(0)}
                            width={width - margin * 2}
                            height={height - getY(0) - margin}
                            fill={tealLight}
                        />
                    </>
                )}
                {/* Area fill for running total */}
                {chartMode === "total" && posStarted && (
                    <path
                        d={posArea}
                        fill="rgba(34,139,34,0.25)"
                        stroke="none"
                    />
                )}
                {chartMode === "total" && negStarted && (
                    <path
                        d={negArea}
                        fill="rgba(230,57,70,0.25)"
                        stroke="none"
                    />
                )}
                {chartMode === "total" && (
                    <path
                        d={totalArea}
                        fill=" rgba(34,139,34,0.18)" // soft green, adjust as needed
                        stroke="none"
                    />
                )}
                {/* Horizontal axis lines */}
                {axisLines}
                {/* Line path */}
                <polyline
                    fill="none"
                    stroke={chartMode === "price" ? teal : "#888"}
                    strokeWidth="2"
                    points={points.map((p, i) => `${getX(i)},${getY(p.y)}`).join(" ")}
                />
                {/* Points */}
                {points.map((p, i) => (
                    <g key={i}>
                        <circle
                            cx={getX(i)}
                            cy={getY(p.y)}
                            r={getRadius(p.qty)}
                            fill={chartMode === "total"
                                ? (p.y >= 0 ? "#228B22" : "#e63946")
                                : teal}
                            onMouseEnter={e => {
                                setHoverIdx(i);
                                setMousePos({
                                    x: e.nativeEvent.clientX,
                                    y: e.nativeEvent.clientY
                                });
                            }}
                            onMouseMove={e => {
                                setMousePos({
                                    x: e.nativeEvent.clientX,
                                    y: e.nativeEvent.clientY
                                });
                            }}
                            onMouseLeave={() => setHoverIdx(null)}
                        />
                        {i % 3 === 0 && (
                            <text
                                x={getX(i)}
                                y={height - 8}
                                fill="#bbb"
                                fontSize={10}
                                textAnchor="middle"
                            >
                                {p.time}
                            </text>
                        )}
                    </g>
                ))}
                {/* Axis labels */}
                {[0, 1, 2, 3, 4].map(i => {
                    const yValue = minY + (i * (maxY - minY) / 4);
                    return (
                        <text
                            key={i}
                            x={margin + 2}
                            y={getY(yValue) - 6}
                            fill="#bbb"
                            fontSize={12}
                            textAnchor="start"
                        >
                            {yValue.toFixed(2)}
                        </text>
                    );
                })}
                {/* Highest/Lowest labels */}
                {points.length > 0 && (
                    <text
                        x={getX(maxIdx) + 8}
                        y={getY(points[maxIdx].y) - 12}
                        fill={chartMode === "total" ? "#228B22" : teal}
                        fontSize={14}
                        fontWeight="bold"
                        textAnchor="start"
                    >
                        {chartMode === "total"
                            ? `Max P&L: ${points[maxIdx].y.toFixed(2)}`
                            : `Max Price: ${points[maxIdx].y.toFixed(2)}`}
                    </text>
                )}
                {points.length > 0 && (
                    <text
                        x={getX(minIdx) + 8}
                        y={getY(points[minIdx].y) + 18}
                        fill={chartMode === "total" ? "#e63946" : teal}
                        fontSize={14}
                        fontWeight="bold"
                        textAnchor="start"
                    >
                        {chartMode === "total"
                            ? `Min P&L: ${points[minIdx].y.toFixed(2)}`
                            : `Min Price: ${points[minIdx].y.toFixed(2)}`}
                    </text>
                )}
            </svg>
            {tooltip}
        </div>
    );
}

// Utility for currency formatting (USD, can be changed as needed)
function formatCurrency(value) {
    return value === "" || value === null || isNaN(value)
        ? ""
        : value.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
}

// ✅ Helper function to convert Risk % to decimal for threshold comparison
// Backend sends: 7.1 meaning 7.1%, we need to convert to 0.071 for comparison
function parseRiskPercentage(riskValue) {
    if (riskValue === null || riskValue === undefined || riskValue === "") return 0;
    const num = Number(riskValue);
    return isNaN(num) ? 0 : num / 100; // Convert percentage to decimal (e.g., 7.1 -> 0.071)
}

function MaxIntradayTrendingChart({ activeDate, screen, refreshKey }) {
    // All hooks first
    const [trades, setTrades] = useState([]);
    const [tooltip, setTooltip] = useState("");
    const [loading, setLoading] = useState(true);
    const [defaultDate, setDefaultDate] = useState(null);
    const [collapsed, setCollapsed] = useState(true);
    const [tableVisible, setTableVisible] = useState(false);
    const [recordLimit, setRecordLimit] = useState("all");
    const [error, setError] = useState("");
    const [chartMode, setChartMode] = useState("price");

    useEffect(() => {
        axios.get(`${API_URL}/api/max-intraday-trending-table`, {
            withCredentials: true
        }).then(res => {
            if (res.data && res.data.oldest_date) {
                setDefaultDate(res.data.oldest_date);
            }
        }).catch(err => {
            setError("Failed to fetch oldest date.");
            handleUIEvent(null, { error: err.message, action: "fetch_oldest_date_error" }, null, "MaxIntradayTrendingChart", "_48_max_intraday_trending_table.js");
        });
    }, []);

    useEffect(() => {
        const dateToUse = activeDate || defaultDate;
        if (!dateToUse) return;
        setLoading(true);
        axios.get(`${API_URL}/api/max-intraday-trending-table`, {
            params: {
                date: dateToUse,
                limit: recordLimit !== "all" ? Number(recordLimit) : undefined,
                refreshKey
            },
            withCredentials: true
        }).then(res => {
            setTrades(res.data.trades || []);
            setTooltip(res.data.tooltip || "");
            setLoading(false);
        }).catch(err => {
            setLoading(false);
            setError("Failed to fetch trades.");
            handleUIEvent(null, { error: err.message, action: "fetch_trades_error" }, null, "MaxIntradayTrendingChart", "_48_max_intraday_trending_table.js");
        });
    }, [activeDate, defaultDate, recordLimit, refreshKey]);

    // Calculate filteredTrades after all hooks
    let filteredTrades = filterTradesToMaxQtyGroup(trades);
    if (recordLimit !== "all") {
        filteredTrades = filteredTrades.slice(0, Number(recordLimit));
    }

    // Hide the component if less than 3 filtered trades
    if (filteredTrades.length < 3) {
        return null;
    }

    const symbol = trades.length > 0 ? trades[0].Symbol : "";
    const maxQty = trades.length > 0 ? trades[0].MaxQty : null;
    const { plSeries, maxQtySeries, runningPosEffectSeries } = calculatePLAndMaxQtySeries(trades);

    function handleTooltipClick(e) {
        handleUIEvent(
            e,
            {
                action: "tooltip_click",
                tooltip,
                button_link_value: "Tooltip"
            },
            null,
            screen,
            "_48_max_intraday_trending_table.js"
        );
    }

    function handleDropdownChange(e) {
        setRecordLimit(e.target.value);
        handleUIEvent(
            e,
            {
                action: "dropdown_change",
                value: e.target.value,
                field: "recordLimit"
            },
            null,
            screen,
            "_48_max_intraday_trending_table.js"
        );
    }

    function handleTableToggle(e) {
        setTableVisible(!tableVisible);
        handleUIEvent(
            e,
            {
                action: "toggle_table",
                visible: !tableVisible,
                button_link_value: !tableVisible ? "▼ Hide Table" : "► Show Table"
            },
            null,
            screen,
            "_48_max_intraday_trending_table.js"
        );
    }

    function handleDownloadClick(e) {
        handleUIEvent(
            e,
            {
                action: "download_csv",
                button_link_value: "Download CSV"
            },
            null,
            screen,
            "_48_max_intraday_trending_table.js"
        );
        downloadCSV(filteredTrades, plSeries.slice(0, filteredTrades.length));
    }

    function handleRowToggle(e) {
        if (e.target.tagName === "INPUT" || e.target.tagName === "BUTTON") return;
        // No-op or add row selection logic if needed
    }

    return (
        <div
            className={`collapse-toggle-container${collapsed ? " collapsed" : ""} max-intraday-trending-container`}
            style={{ color: "#fff", background: "#222", padding: 24, borderRadius: 8 }}
        >
            {/* Header */}
            <h4
                className="collapse-toggle-headline"
                onClick={() => setCollapsed(!collapsed)}
                style={{ cursor: "pointer", margin: 0, marginBottom: collapsed ? 0 : 16,zIndex:1 }}
            >
                📈 Largest Intra Day Position
                <span onClick={handleTooltipClick}><Tooltip text={tooltip} /></span>
            </h4>

            {/* Collapsible section */}
            <div className="collapse-content" style={{ display: collapsed ? "none" : "block" }}>
                {/* Filter controls and chart mode buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
                    <label style={{ color: "#fff" }}>Show:</label>
                    <select
                        value={recordLimit}
                        onChange={handleDropdownChange}
                        style={{ padding: "4px 8px", borderRadius: 4, border: "1px solid #888" }}
                    >
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="all">All</option>
                    </select>
                    <button
                        style={buttonStyle(chartMode === "total")}
                        onClick={() => setChartMode("total")}
                    >
                        Running Total
                    </button>
                    <button
                        style={buttonStyle(chartMode === "price")}
                        onClick={() => setChartMode("price")}
                    >
                        Price
                    </button>
                </div>

                {/* Chart always visible */}
                {loading ? (
                    <div>Loading...</div>
                ) : trades.length === 0 ? (
                    <div>No trades found for this date.</div>
                ) : (
                    <>
                        <RunningPLChart
                            trades={filteredTrades}
                            plSeries={plSeries.slice(0, filteredTrades.length)}
                            chartMode={chartMode}
                        />

                        {/* Table section - hidden by default */}
                        <div style={{ marginTop: 16 }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                                <button
                                    style={{
                                        padding: "6px 12px",
                                        background: tableVisible ? "#228B22" : "#e63946",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: 4,
                                        cursor: "pointer",
                                        fontWeight: 600
                                    }}
                                    onClick={handleTableToggle}
                                >
                                    {tableVisible ? "▼ Hide Table" : "► Show Table"}
                                </button>
                                {tableVisible && (
                                    <button
                                        style={{
                                            padding: "8px 16px",
                                            background: "#e63946",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: 4,
                                            cursor: "pointer"
                                        }}
                                        onClick={handleDownloadClick}
                                        disabled={loading || filteredTrades.length === 0}
                                    >
                                        Download CSV
                                    </button>
                                )}
                                {tableVisible && (
                                    <div style={{ color: "#e63946", fontWeight: "bold", fontSize: 18 }}>
                                        {symbol} &mdash; Max Intra Day Qty: {maxQty}
                                    </div>
                                )}
                            </div>

                            {/* Details table - visible only when tableVisible is true */}
                            {tableVisible && (
                                <div style={{
                                    maxHeight: 420, // adjust as needed for your layout
                                    overflowY: "auto",
                                    marginBottom: 16,
                                    borderRadius: 8,
                                    border: "1px solid #444",
                                    background: "#222"
                                }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", background: "#222" }}>
                                        <thead>
                                            <tr style={{ background: "#333", color: "#fff" }}>
                                                <th style={{ padding: "8px 4px", textAlign: "left" }}>Exec Time</th>
                                                <th style={{ textAlign: "left" }}>Side</th>
                                                <th style={{ textAlign: "left" }}>Qty</th>
                                                <th style={{ textAlign: "left" }}>Price</th>
                                                <th style={{ textAlign: "left" }}>Pos Effect</th>
                                                <th style={{ textAlign: "left" }}>Risk %</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trades.map((trade, idx) => {
                                                let execTime = "";
                                                if (trade.Exec_Time) {
                                                    let dt = new Date(trade.Exec_Time.replace(/-/g, " "));
                                                    if (!isNaN(dt.getTime())) {
                                                        const pad = n => n.toString().padStart(2, "0");
                                                        execTime = `${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
                                                    } else {
                                                        execTime = trade.Exec_Time;
                                                    }
                                                }

                                                // ✅ Convert risk percentage to decimal for threshold comparison
                                                const riskDecimal = parseRiskPercentage(trade["Risk %"]);
                                                const isAboveThreshold = riskDecimal > CONCENTRATION_THRESHOLD_1;
                                                const riskCellColor = isAboveThreshold ? "#e63946" : "#fff";

                                                // ✅ Check if this row reaches the day's highest quantity
                                                const dayMaxQty = Math.max(...trades.map(t => Number(t.RunningQty)));
                                                const isMaxQtyRow = Number(trade.RunningQty) === dayMaxQty;
                                                const rowBorderStyle = isMaxQtyRow ? "3px solid #e63946" : "none";

                                                return (
                                                    <tr key={trade.id} style={{ cursor: "pointer", background: "#222", border: rowBorderStyle }} onClick={handleRowToggle}>
                                                        <td style={{ padding: "8px 4px" }}>{execTime}</td>
                                                        <td>{trade.Side}</td>
                                                        <td style={{ padding: "8px 4px" }}>{trade.Qty}</td>
                                                        <td>{trade.price}</td>
                                                        <td>{trade.Pos_Effect || runningPosEffectSeries[idx]}</td>
                                                        <td style={{ color: riskCellColor, fontWeight: "bold" }}>
                                                            {trade["Risk %"] !== undefined && trade["Risk %"] !== null && trade["Risk %"] !== ""
                                                                ? Number(trade["Risk %"]).toFixed(2) + "%"
                                                                : ""}
                                                        </td>
                                                      
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Error log event */}
            {error && handleUIEvent(null, { error, action: "render_error" }, null, "MaxIntradayTrendingChart", "_48_max_intraday_trending_table.js")}
        </div>
    );
}

function filterTradesToMaxQtyGroup(trades) {
    if (!trades.length) return [];
    // Find the max running qty for the day
    const maxQty = Math.max(...trades.map(t => Number(t.RunningQty)));
    let groups = [];
    let currentGroup = [];
    let lastQty = 0;
    for (let i = 0; i < trades.length; i++) {
        const qty = Number(trades[i].RunningQty);
        // Start new group if lastQty was 0 and current is not 0
        if (lastQty === 0 && qty !== 0) {
            currentGroup = [];
        }
        currentGroup.push(trades[i]);
        // End group if qty returns to 0 or at last trade
        const nextQty = i < trades.length - 1 ? Number(trades[i + 1].RunningQty) : 0;
        if (qty === 0 || i === trades.length - 1 || nextQty === 0) {
            groups.push(currentGroup);
            currentGroup = [];
        }
        lastQty = qty;
    }
    // Find the group that contains the maxQty
    const groupWithMax = groups.find(g => g.some(t => Number(t.RunningQty) === maxQty));
    return groupWithMax || [];
}

export default MaxIntradayTrendingChart;